import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Download,
  FileText,
  Mail,
  MoreHorizontal,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react'
import type { DealDocument } from '@/services/documents'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import type { SponsorPortfolio } from '@/services/dossierContext'
import {
  dealPartyService,
  partiesToCsv,
  type DealParty,
} from '@/services/dealParties'
import { loansService } from '@/services/loans'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { useDossierEdit } from './editContext'
import { ValueStateChip } from './valueStateChip'
import type { FieldSourceRef } from './workspace/types'
import { AddPartyDialog } from './parties/AddPartyDialog'
import { BrokerLinkPanel } from './parties/BrokerLinkPanel'
import type { BrokerResolution } from '@/services/ingestion'
import { EditPartyDialog } from './parties/EditPartyDialog'
import { RelationshipMap } from './parties/RelationshipMap'
import { PartyDialog } from './parties/PartyDialog'
import OrganizationPeople from '@/components/crm/OrganizationPeople'
import {
  PARTY_GROUPS,
  SCREENING_CHECKS,
  companyKeyOf,
  entityContact,
  entityKeyOf,
  fact,
  humanDate,
  initials,
  orgKeyOf,
  partyColor,
  partyGroup,
  partyHasKind,
  partyName,
  partyRoles,
  safeWebUrl,
} from './parties/partyViewModel'
import './parties/parties-intelligence.css'

export const PARTY_FILTERS = [
  'All parties',
  'Sponsor / Borrower',
  'Borrowing Entity',
  'Guarantor',
  'Principal',
  'Broker',
  'Other party',
  'Internal team',
] as const
interface Props {
  loanId: string
  sponsorPortfolio?: SponsorPortfolio | null
  /** The intake record's broker proposal, when the deal has one waiting for
   *  a person to confirm it (governance rule Q1). */
  brokerLink?: {
    ingestionId: string
    resolution: BrokerResolution | null | undefined
    brokerId?: string | null
    brokerFirmId?: string | null
  } | null
  documents?: DealDocument[]
  activity?: LoanActivityEvent[]
  /** What the intake package said about the sponsor ("repeat
   *  client, closed 4 deals with us…"). Read from the linked intake record;
   *  it is the sender's remark, not a verified fact. */
  sponsorRemark?: string | null
}
type Dialog = 'history' | 'findings' | 'log' | 'remove' | 'interaction' | null

/** One line of a party's contact history, from whichever store holds it.
 *
 *  The list on the page truncates the subject and the body to one ellipsed
 *  line each, which is all a four-row summary has room for — and for an email
 *  whose preview is the only body on record, that ellipsis was the whole
 *  message. `source` and `direction` travel with the row so the full view can
 *  say where the line came from rather than presenting three different stores
 *  as one undifferentiated feed. */
type Interaction = {
  id: string
  kind: string
  subject: string
  detail: string
  at: string | null | undefined
  owner: string | null | undefined
  /** Which store this line came from, named for the reader. */
  source: string
  direction?: string | null
}

function InteractionIcon({ kind }: { kind: string }) {
  if (/call/i.test(kind)) return <Phone size={16} />
  if (/meeting/i.test(kind)) return <Users size={16} />
  if (/note/i.test(kind)) return <FileText size={16} />
  return <Mail size={16} />
}
function Panel({
  title,
  children,
  action,
  className = '',
}: {
  title: string
  children: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <section className={`pi-panel ${className}`}>
      <header className="pi-panel-heading">
        <h3>{title}</h3>
        {action}
      </header>
      {children}
    </section>
  )
}
function Avatar({
  party,
  large = false,
}: {
  party: DealParty
  large?: boolean
}) {
  const [failed, setFailed] = useState<string | null>(null)
  const url = safeWebUrl(party.portraitUrl)
  return (
    <div
      className={`pi-avatar ${large ? 'pi-avatar-large' : ''}`}
      aria-label={`${partyName(party)} ${url && failed !== url ? 'portrait' : 'initials'}`}
    >
      {url && failed !== url ? (
        <img src={url} alt={partyName(party)} onError={() => setFailed(url)} />
      ) : (
        initials(partyName(party))
      )}
    </div>
  )
}

/** Two spellings of one name: case, spacing and punctuation aside. */
function sameName(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) =>
    typeof v === 'string' ? v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() : ''
  const left = norm(a)
  return left !== '' && left === norm(b)
}

/**
 * The value state of a party whose name is a deal field (C05). A sponsor the
 * platform copied from the stated borrowing entity is the field's Suggested
 * value and says so; a stated name carries no chip. The field's state is lent
 * only when the party's name IS that field's saved value — a CRM name that
 * differs is not the figure the lineage describes.
 */
function usePartyValueSource(): (party: DealParty) => FieldSourceRef | null {
  const { valueSource, effectiveValue } = useDossierEdit()
  return (party) => {
    if (!valueSource) return null
    for (const field of party.dealFields ?? []) {
      if (!sameName(effectiveValue(field), party.name)) continue
      const source = valueSource(field)
      if (source) return source
    }
    return null
  }
}

export function PartiesPage({
  loanId,
  sponsorPortfolio,
  brokerLink = null,
  activity = [],
  sponsorRemark = null,
}: Props) {
  const { unlocked } = useDossierEdit()
  const partySource = usePartyValueSource()
  const queryClient = useQueryClient()
  const openDossier = useDealDossierStore((state) => state.openDossier)
  const setTab = useDealDossierStore((state) => state.setTab)
  const notes = useQuery({
    queryKey: ['loan-notes', loanId],
    queryFn: () => loansService.listNotes(loanId),
  })
  const roster = useQuery({
    queryKey: ['deal-parties', loanId],
    queryFn: () => dealPartyService.list(loanId),
    staleTime: 30_000,
  })
  const parties = useMemo(() => roster.data?.parties ?? [], [roster.data])
  // Sponsor / Borrower is required. When the deal names none the section used
  // to vanish, which reads as "nothing to see" rather than "missing".
  const sponsorMissing = Boolean(
    roster.data &&
      (roster.data.missingRequired
        ? roster.data.missingRequired.some((m) => m.id === 'sponsor')
        : !parties.some((p) => p.id === 'sponsor')),
  )
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('All parties')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [openInteraction, setOpenInteraction] = useState<Interaction | null>(null)
  /** The organisation whose people are open in a dialog — a firm a person is
   *  recorded at, opened from the map or from Related Entities. */
  const [orgView, setOrgView] = useState<string | null>(null)
  const [menu, setMenu] = useState(false)
  const [contactMenu, setContactMenu] = useState(false)
  const [note, setNote] = useState('')
  const [kind, setKind] = useState('Call')
  const [error, setError] = useState<string | null>(null)
  const rosterRef = useRef<HTMLDivElement>(null)
  const filtered = parties.filter(
    (p) =>
      (filter === 'All parties' || partyHasKind(p, filter)) &&
      [partyName(p), p.name, p.company, ...partyRoles(p)]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase()),
  )
  const selected =
    parties.find((p) => p.id === selectedId) ??
    parties.find((p) => !p.isEntity || p.contact) ??
    parties[0]
  const interactions = useMemo<Interaction[]>(
    () =>
      (selected?.communications ?? []).map((m) => ({
        id: m.id,
        kind: m.type || 'Message',
        subject: m.subject || m.type,
        detail: m.preview || m.direction || 'No description recorded',
        at: m.timestamp,
        owner: m.user,
        source: 'CRM correspondence',
        direction: m.direction,
      })),
    [selected],
  )
  // General deal activity must not be misattributed to the selected person.
  const logged = activity
    .filter(
      (e) =>
        selected &&
        Object.values(e.details ?? {}).some(
          (v) => typeof v === 'string' && v.includes(`[Party ${selected.id}]`),
        ),
    )
    .map<Interaction>((e) => ({
      id: String(e.id),
      kind: e.eventType,
      subject: 'Logged interaction',
      detail: String(
        e.details?.body ?? e.details?.note ?? e.details?.summary ?? e.eventType,
      ),
      at: e.createdAt,
      owner: e.userEmail,
      source: 'Deal activity log',
    }))
  const journal = (notes.data ?? [])
    .filter((n) => selected && n.body.startsWith(`[Party ${selected.id}]`))
    .map<Interaction>((n) => ({
      id: `note-${n.id}`,
      kind: n.body.match(/· ([^:]+):/)?.[1] || 'Note',
      subject: 'Logged interaction',
      detail: n.body.replace(/^\[Party [^\]]+\] /, ''),
      at: n.createdAt,
      owner: n.author,
      source: 'Deal journal',
    }))
  const history: Interaction[] = [
    ...interactions,
    ...journal,
    ...logged.filter((e) => !journal.some((n) => n.detail === e.detail)),
  ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')))
  /** The roster's own entries at an organisation: the firm itself, and
   *  every person recorded there. */
  function onDealAt(name: string) {
    const key = orgKeyOf(name)
    const orgKey =
      parties.find((p) => p.isEntity && orgKeyOf(p.name) === key)?.partyKey ?? key
    return parties
      .filter(
        (p) =>
          (p.isEntity && (entityKeyOf(p) === orgKey || orgKeyOf(p.name) === key)) ||
          (!p.isEntity && (companyKeyOf(p) === orgKey || companyKeyOf(p) === key)),
      )
      .map((p) => ({
        id: p.id,
        name: p.isEntity ? p.name : partyName(p),
        contact: p.isEntity ? p.contact : null,
        roles: partyRoles(p),
      }))
  }
  const portfolio =
    selected && /sponsor|borrower/i.test(selected.kind)
      ? (sponsorPortfolio?.deals ?? []).filter(
          (d) =>
            !d.isCurrent &&
            !/funded|closed|repaid|killed|declined|withdrawn/i.test(d.stage),
        )
      : []
  async function refresh() {
    await Promise.all(
      [
        ['deal-parties', loanId],
        ['crm', 'contacts'],
        ['loan', loanId],
        ['loan-activity', loanId],
        ['loan-notes', loanId],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }
  const log = useMutation({
    mutationFn: () =>
      loansService.addNote(
        loanId,
        `[Party ${selected!.id}] ${partyName(selected!)} · ${kind}: ${note.trim()}`,
      ),
    onSuccess: async () => {
      setNote('')
      setDialog(null)
      await refresh()
    },
    onError: () =>
      setError(
        'The interaction could not be saved. Your text is retained; please retry.',
      ),
  })
  const remove = useMutation({
    mutationFn: () =>
      selected!.alsoSlots?.length
        ? dealPartyService.remove(
            loanId,
            selected!.id,
            selected!.contactId,
            selected!.alsoSlots,
          )
        : dealPartyService.remove(loanId, selected!.id, selected!.contactId),
    onSuccess: async () => {
      setDialog(null)
      setSelectedId(null)
      await refresh()
    },
    onError: () => setError('The party could not be removed. Please retry.'),
  })
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        (adding || editing || menu || contactMenu)
      ) {
        event.stopImmediatePropagation()
        setAdding(false)
        setEditing(false)
        setMenu(false)
        setContactMenu(false)
      }
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [adding, editing, menu, contactMenu])
  function choose(id: string) {
    setSelectedId(id)
    setContactMenu(false)
  }
  function exportCsv() {
    if (!roster.data) return
    const url = URL.createObjectURL(
      new Blob([partiesToCsv(roster.data)], { type: 'text/csv;charset=utf-8' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = `${loanId}-parties.csv`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const lockMessage = unlocked
    ? undefined
    : 'Unlock the dossier to make changes'
  return (
    <div className="parties-intelligence">
      <div className="pi-titlebar">
        <div>
          <h2>Parties &amp; Relationship Intelligence</h2>
          <p>
            People, organizations and relationships for this transaction, from
            the deal record and your CRM.
          </p>
        </div>
        <div className="pi-page-actions">
          <div>
            <button
              className="pi-gold-outline"
              onClick={() => setAdding(true)}
              disabled={!unlocked}
              title={lockMessage}
            >
              <Plus size={16} /> Add Party
            </button>
            <button onClick={exportCsv} disabled={!roster.data}>
              <Download size={16} /> Export
            </button>
            <div className="pi-menu-anchor">
              <button
                aria-label="More party actions"
                aria-expanded={menu}
                onClick={() => setMenu(!menu)}
              >
                <MoreHorizontal size={18} />
              </button>
              {menu && (
                <div className="pi-menu">
                  <button
                    onClick={() => {
                      void roster.refetch()
                      setMenu(false)
                    }}
                  >
                    Refresh roster
                  </button>
                </div>
              )}
            </div>
          </div>
          <small>
            {roster.dataUpdatedAt
              ? `As of ${humanDate(new Date(roster.dataUpdatedAt).toISOString())}`
              : 'Reading roster…'}
          </small>
        </div>
      </div>
      {error && (
        <div role="alert" className="pi-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {roster.data?.identitySuppression?.staffConfigured === false ? (
        // The same warning the CRM directory carries, on the tab where the
        // desk actually sees its own people filed as outside parties.
        <div role="note" aria-label="Identity suppression not configured" className="pi-note">
          Identity suppression is not configured, so your own staff and intake mailbox can appear on this
          roster as outside parties (brokers, &ldquo;Other party&rdquo;). List your firm&apos;s email domains and
          people under{' '}
          <a href="/settings">Settings → Platform Settings → Tenant Identity Suppression</a>, then use Re-read
          deals in the CRM.
        </div>
      ) : null}
      {brokerLink ? (
        <BrokerLinkPanel
          loanId={loanId}
          ingestionId={brokerLink.ingestionId}
          resolution={brokerLink.resolution}
          brokerId={brokerLink.brokerId}
          brokerFirmId={brokerLink.brokerFirmId}
          locked={!unlocked}
        />
      ) : null}
      {sponsorRemark?.trim() ? (
        // The package's remark on the sponsor reached only the intake record;
        // no tab of the deal showed it (nd-057).
        <div role="note" aria-label="Remark on the sponsor" className="pi-remark">
          <span className="pi-remark-label">Remark on the sponsor</span>
          <q>{sponsorRemark.trim()}</q>
          <small>Stated in the intake package · the sender&apos;s words, not verified</small>
        </div>
      ) : null}
      {roster.isLoading ? (
        <div role="status" className="pi-state">
          Reading the deal’s parties…
        </div>
      ) : roster.isError ? (
        <div role="alert" className="pi-state">
          The party roster could not be read. Nothing has been changed.
          <button onClick={() => void roster.refetch()}>Retry</button>
        </div>
      ) : (
        <div className="pi-workspace">
          <Panel
            title={`Deal Parties · ${parties.length}`}
            className="pi-roster"
          >
            <div className="pi-search">
              <label>
                <Search size={16} />
                <input
                  aria-label="Search parties"
                  placeholder="Search by name, company, or role…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <select
                aria-label="Party type"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                {[
                  ...new Set([...PARTY_FILTERS, ...parties.map((p) => p.kind)]),
                ].map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="pi-roster-list" ref={rosterRef}>
              {PARTY_GROUPS.map((group) => {
                const members = filtered.filter((p) => partyGroup(p) === group)
                const notice = group === PARTY_GROUPS[0] && sponsorMissing
                return members.length || notice ? (
                  <section key={group}>
                    <h4>
                      {group} ({members.length})
                    </h4>
                    {notice && (
                      <p className="pi-required-missing" role="note">
                        <strong>Sponsor / Borrower not stated.</strong> A
                        required party: the package names no sponsor, or names
                        rivals nobody has settled yet.
                        {/* Laid out while locked too, invisible: added only on
                            unlock, the sentence wrapped onto a third line at
                            1366 and the roster below moved down 16px
                            (Brixton Commons, 21 Oct). */}
                        <span className={unlocked ? undefined : 'invisible'} aria-hidden={unlocked ? undefined : true}>
                          {' '}Add the sponsor with Add party.
                        </span>
                      </p>
                    )}
                    {members.map((p) => (
                      <div
                        key={p.id}
                        className={`pi-party-row ${selected?.id === p.id ? 'is-selected' : ''}`}
                      >
                        <button
                          className="pi-party-select"
                          aria-pressed={selected?.id === p.id}
                          onClick={() => choose(p.id)}
                          onKeyDown={(event) => {
                            const buttons = Array.from(
                              rosterRef.current?.querySelectorAll<HTMLButtonElement>(
                                '.pi-party-select',
                              ) ?? [],
                            )
                            const index = buttons.indexOf(event.currentTarget)
                            const next =
                              event.key === 'ArrowDown'
                                ? Math.min(index + 1, buttons.length - 1)
                                : event.key === 'ArrowUp'
                                  ? Math.max(index - 1, 0)
                                  : event.key === 'Home'
                                    ? 0
                                    : event.key === 'End'
                                      ? buttons.length - 1
                                      : -1
                            if (next >= 0) {
                              event.preventDefault()
                              buttons[next].focus()
                              buttons[next].click()
                            }
                          }}
                        >
                          <Avatar party={p} />
                          <span className="pi-party-identity">
                            <strong>
                              {partyName(p)}{' '}
                              <span
                                className={
                                  p.needsReview
                                    ? 'pi-review-dot'
                                    : 'pi-record-dot'
                                }
                                title={
                                  p.needsReview
                                    ? 'Contact review required'
                                    : 'Verification not on record'
                                }
                              >
                                ○
                              </span>
                              <ValueStateChip source={partySource(p)} />
                            </strong>
                            <small>
                              {p.company ||
                                (p.isEntity
                                  ? 'Organization'
                                  : 'Organization not on record')}
                              {entityContact(p)
                                ? ` · Contact: ${entityContact(p)}`
                                : ''}
                            </small>
                          </span>
                          <span
                            className="pi-role"
                            style={{
                              color: partyColor(p),
                              borderColor: `${partyColor(p)}55`,
                            }}
                            title={partyRoles(p).join(', ')}
                          >
                            {partyRoles(p).join(' / ')}
                          </span>
                        </button>
                        <div className="pi-shortcuts">
                          {p.email ? (
                            <a
                              href={`mailto:${p.email}`}
                              aria-label={`Email ${partyName(p)}`}
                            >
                              <Mail size={16} />
                            </a>
                          ) : (
                            <span title="Email not on record">
                              <Mail size={16} />
                            </span>
                          )}
                          {p.phone ? (
                            <a
                              href={`tel:${p.phone.replace(/[^+\d]/g, '')}`}
                              aria-label={`Call ${partyName(p)}`}
                            >
                              <Phone size={16} />
                            </a>
                          ) : (
                            <span title="Phone not on record">
                              <Phone size={16} />
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </section>
                ) : null
              })}
              {!filtered.length && (
                <div className="pi-empty">
                  {parties.length
                    ? 'No parties match your search and filter.'
                    : 'No parties are recorded on this deal.'}
                  {parties.length > 0 && (
                    <button
                      onClick={() => {
                        setSearch('')
                        setFilter('All parties')
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
            {!unlocked && (
              <p className="pi-lock-note">
                Unlock the dossier to add or edit parties.
              </p>
            )}
          </Panel>
          <Panel title="Contact Intelligence" className="pi-contact">
            {selected ? (
              <>
                <div className="pi-contact-scroll">
                  <div className="pi-contact-summary">
                    <Avatar party={selected} large />
                    <div className="pi-contact-heading">
                      <h3>
                        {/* A party's name is a deal field, but it is not edited
                            through the dossier draft: one entity can fill
                            several slots (sponsor and guarantor), and a rename
                            must write all of them and its CRM contact at once.
                            Unlocked, the name opens the party's own editor —
                            the path that does — in place, rather than behind
                            the contact menu (owner, 21 Oct). */}
                        {unlocked ? (
                          <span
                            role="button"
                            tabIndex={0}
                            data-edit-party={selected.id}
                            title={`Edit ${partyName(selected)}: name, organization, contact details`}
                            onClick={() => setEditing(true)}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              event.preventDefault()
                              setEditing(true)
                            }}
                            className="dossier-edit-trigger"
                          >
                            {partyName(selected)}
                          </span>
                        ) : (
                          partyName(selected)
                        )}{' '}
                        <ValueStateChip source={partySource(selected)} />
                      </h3>
                      <span className="pi-verification">
                        {selected.needsReview
                          ? 'Review required'
                          : 'Verification not on record'}
                      </span>
                      <p>
                        {selected.title ||
                          (selected.isEntity
                            ? 'Organization'
                            : 'Title not on record')}
                      </p>
                      {/* An organisation is not "at" an organisation: its
                          people are listed below instead. */}
                      {selected.company || !selected.isEntity ? (
                        <p className="pi-link-color">
                          {selected.company || 'Organization not on record'}
                        </p>
                      ) : null}
                      {entityContact(selected) ? (
                        <p>Contact: {entityContact(selected)}</p>
                      ) : null}
                      <p>
                        {selected.location ||
                          selected.address ||
                          'Location not on record'}
                      </p>
                      <div className="pi-contact-links">
                        {selected.email ? (
                          <a href={`mailto:${selected.email}`}>
                            <Mail size={15} />
                            {selected.email}
                          </a>
                        ) : (
                          <span>Email not on record</span>
                        )}
                        {selected.phone ? (
                          <a
                            href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`}
                          >
                            <Phone size={15} />
                            {selected.phone}
                          </a>
                        ) : (
                          <span>Phone not on record</span>
                        )}
                        {safeWebUrl(selected.linkedIn) && (
                          <a
                            href={safeWebUrl(selected.linkedIn)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            LinkedIn ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="pi-contact-actions">
                    {selected.email ? (
                      <a
                        className="pi-primary"
                        href={`mailto:${selected.email}`}
                      >
                        <Mail size={16} />
                        Email
                      </a>
                    ) : (
                      <button disabled title="Email not on record">
                        Email unavailable
                      </button>
                    )}
                    {selected.phone ? (
                      <a href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`}>
                        <Phone size={16} />
                        Call
                      </a>
                    ) : (
                      <button disabled title="Phone not on record">
                        Call unavailable
                      </button>
                    )}
                    <button
                      onClick={() => setDialog('log')}
                      disabled={!unlocked}
                      title={lockMessage}
                    >
                      Log Interaction
                    </button>
                    <div className="pi-menu-anchor">
                      <button
                        aria-label="Contact actions"
                        onClick={() => setContactMenu(!contactMenu)}
                        aria-expanded={contactMenu}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {contactMenu && (
                        <div className="pi-menu">
                          <button
                            disabled={!unlocked}
                            onClick={() => {
                              setEditing(true)
                              setContactMenu(false)
                            }}
                          >
                            Edit party
                          </button>
                          <button
                            disabled={!unlocked}
                            onClick={() => {
                              setDialog('remove')
                              setContactMenu(false)
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <dl className="pi-facts">
                    {[
                      ['Deal role', partyRoles(selected).join(' / ')],
                      [
                        'Type',
                        `${selected.isEntity ? 'Organization' : 'Person'}${selected.entityBasis ? ` — ${selected.entityBasis}` : ''}`,
                      ],
                      ...(selected.companyBasis && selected.company
                        ? [['Organization source', selected.companyBasis]]
                        : []),
                      [
                        'Decision authority',
                        fact(selected, 'Decision authority'),
                      ],
                      [
                        'Signing authority',
                        fact(selected, 'Signing authority'),
                      ],
                      ['Ownership %', fact(selected, 'Ownership %')],
                      ['Source', selected.contactSource || 'Deal record'],
                      ['Introduced by', fact(selected, 'Introduced by')],
                      ['Last verified', fact(selected, 'Last verified')],
                      ['Contact owner', fact(selected, 'Contact owner')],
                      [
                        'CRM linkage',
                        selected.contactId
                          ? 'Linked to CRM'
                          : 'No linked contact',
                      ],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                  <details className="pi-secondary">
                    <summary>Additional contact details</summary>
                    <dl className="pi-facts">
                      {[
                        ['Mobile', selected.phones.mobile],
                        ['Direct', selected.phones.direct],
                        ['Office', selected.phones.office],
                        ...selected.facts,
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value || 'Not on record'}</dd>
                        </div>
                      ))}
                    </dl>
                    {safeWebUrl(selected.website) && (
                      <p>
                        <a
                          href={safeWebUrl(selected.website)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {selected.website}
                        </a>
                        {selected.websiteBasis === 'email_domain' && (
                          <small> from email</small>
                        )}
                      </p>
                    )}
                  </details>
                  {selected.isEntity && (
                    <section className="pi-org-people" aria-label="People under this organization">
                      <h4>People under this organization</h4>
                      <OrganizationPeople
                        name={selected.name}
                        loanId={loanId}
                        onDeal={onDealAt(selected.name)}
                        onOpenDeal={(id) => openDossier(id, 'parties')}
                      />
                    </section>
                  )}
                  <section className="pi-involvement">
                    <h4>Deal Involvement</h4>
                    <p>
                      {selected.notes ||
                        `${partyName(selected)} is recorded as ${partyRoles(selected).join(' / ')} on this transaction. ${selected.capacities.length ? `Recorded capacity: ${selected.capacities.join(', ')}.` : 'Additional responsibilities are not on record.'}`}
                    </p>
                  </section>
                  <section className="pi-interactions">
                    <div className="pi-subheading">
                      <h4>Recent Interactions ({history.length})</h4>
                      <button onClick={() => setDialog('history')}>
                        View all →
                      </button>
                    </div>
                    {history.length ? (
                      history.slice(0, 4).map((item) => (
                        /* The row ellipses the subject and the body to one
                           line each. Opening it is how a reader gets the rest
                           — a button, so the keyboard reaches it too. */
                        <button
                          type="button"
                          className="pi-interaction pi-interaction-open"
                          key={item.id}
                          aria-label={`Open interaction: ${item.subject}`}
                          onClick={() => {
                            setOpenInteraction(item)
                            setDialog('interaction')
                          }}
                        >
                          <time>
                            {humanDate(item.at)}
                            <small className="pi-interaction-type">
                              {item.kind}
                            </small>
                          </time>
                          <InteractionIcon kind={item.kind} />
                          <div>
                            <strong>{item.subject}</strong>
                            <p>{item.detail}</p>
                          </div>
                          <small title={item.owner || 'Owner not on record'}>
                            {item.owner ? initials(item.owner) : '—'}
                          </small>
                        </button>
                      ))
                    ) : (
                      <p className="pi-empty">
                        No interactions recorded for this party.
                      </p>
                    )}
                    {notes.isError && (
                      <p role="status" className="pi-empty">
                        Journal notes are temporarily unavailable. Contact
                        correspondence is shown above.
                      </p>
                    )}
                  </section>
                </div>
                <div className="pi-related">
                  <section>
                    <h4>Related Entities</h4>
                    {selected.company || selected.isEntity ? (
                      <button
                        type="button"
                        className="pi-org-link"
                        onClick={() =>
                          setOrgView(selected.company || selected.name)
                        }
                        aria-label={`People under ${selected.company || selected.name}`}
                      >
                        {selected.company || selected.name}
                      </button>
                    ) : (
                      <strong>Not on record</strong>
                    )}
                    <p>
                      {selected.company
                        ? 'Recorded organization · This deal'
                        : 'No additional entity recorded'}
                    </p>
                  </section>
                  <section>
                    <h4>Other Active Deals ({portfolio.length})</h4>
                    {portfolio.length ? (
                      portfolio.map((d) => (
                        <a
                          key={d.loanId}
                          href={`/pipeline?deal=${encodeURIComponent(d.loanId)}&tab=parties`}
                          onClick={(e) => {
                            e.preventDefault()
                            openDossier(d.loanId, 'parties')
                          }}
                        >
                          {d.propertyName} · {d.stage}
                        </a>
                      ))
                    ) : (
                      <p>No other deals available for this party.</p>
                    )}
                  </section>
                </div>
              </>
            ) : (
              <p className="pi-empty">
                Add a party to view contact intelligence.
              </p>
            )}
          </Panel>
          <div className="pi-right">
            <Panel title="Relationship Map" className="pi-map">
              <div className="pi-map-open-surface">
                <button
                  type="button"
                  className="pi-map-open-button"
                  aria-label="Expand relationship map"
                  title="Open relationship map"
                  onClick={() => setExpanded(true)}
                  disabled={!parties.length}
                />
                {roster.data && (
                  <RelationshipMap
                    roster={roster.data}
                    embedded
                    selectedId={selected?.id}
                    onOpenOrganization={setOrgView}
                    onSelect={(id) => {
                      choose(id)
                      setFilter('All parties')
                      setSearch('')
                    }}
                  />
                )}
              </div>
            </Panel>
            <Panel title="Party Intelligence" className="pi-intelligence">
              <div className="pi-intelligence-body">
                <div className="pi-overall">
                  <div className="pi-score-ring">
                    <strong>—</strong>
                    <small>/100</small>
                  </div>
                  <div>
                    <span>Overall Score</span>
                    <h4>Not assessed</h4>
                    <p>
                      No sourced overall assessment is available
                      {selected ? ` for ${partyName(selected)}` : ''}.
                    </p>
                  </div>
                </div>
                <div className="pi-scores">
                  {['Relationship', 'Reputation', 'Risk'].map((label) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>
                        — <small>/ 100</small>
                      </strong>
                      <div className="pi-score-track" />
                      <small>
                        {label === 'Risk'
                          ? 'Higher = greater risk'
                          : 'Not assessed'}
                      </small>
                    </div>
                  ))}
                </div>
                <div className="pi-subheading">
                  <h4>Screening Status</h4>
                  <small>Last screened: Not on record</small>
                </div>
                <div className="pi-screening-layout">
                  <dl className="pi-screening">
                    {SCREENING_CHECKS.map((label) => (
                      <div key={label}>
                        <dt>
                          <ShieldCheck size={14} />
                          {label}
                        </dt>
                        <dd>Not screened</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="pi-findings">
                    <h4>Findings unavailable</h4>
                    <p>
                      Material issue count, severity and finding dates have not
                      been supplied. No clearance is implied.
                    </p>
                    <button onClick={() => setDialog('findings')}>
                      View details →
                    </button>
                  </div>
                </div>
                <div className="pi-sources">
                  <h4>Sources</h4>
                  {(roster.data?.sourcedFrom ?? []).map((source) => (
                    <span key={source}>
                      {source === 'deal_contacts'
                        ? 'Internal CRM'
                        : source === 'brokers'
                          ? 'Brokers'
                          : source === 'sponsors'
                            ? 'Sponsors'
                            : source === 'loans'
                              ? 'Deal record'
                              : source}
                    </span>
                  ))}
                  <small>
                    Roster provenance only; screening sources unavailable.
                  </small>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
      {adding && (
        <AddPartyDialog
          loanId={loanId}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false)
            await refresh()
          }}
        />
      )}
      {editing && selected && (
        <EditPartyDialog
          loanId={loanId}
          party={selected}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false)
            await refresh()
          }}
        />
      )}
      {expanded && roster.data && (
        <RelationshipMap
          roster={roster.data}
          selectedId={selected?.id}
          onClose={() => setExpanded(false)}
          onSelect={(id) => choose(id)}
          onOpenOrganization={(name) => {
            setExpanded(false)
            setOrgView(name)
          }}
        />
      )}
      {orgView && (
        <PartyDialog title={`People under ${orgView}`} onClose={() => setOrgView(null)}>
          <OrganizationPeople
            name={orgView}
            loanId={loanId}
            onDeal={onDealAt(orgView)}
            onOpenDeal={(id) => {
              setOrgView(null)
              openDossier(id, 'parties')
            }}
          />
        </PartyDialog>
      )}
      {dialog && (
        <PartyDialog
          title={
            dialog === 'log'
              ? 'Log interaction'
              : dialog === 'history'
                ? 'All interactions'
                : dialog === 'interaction'
                  ? openInteraction?.subject || 'Interaction'
                  : dialog === 'remove'
                    ? 'Remove party'
                    : 'Screening details'
          }
          onClose={() => {
            setDialog(null)
            setOpenInteraction(null)
          }}
        >
          {dialog === 'log' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (note.trim()) log.mutate()
              }}
            >
              <p>
                Record an attributed note in this deal’s journal for{' '}
                {selected && partyName(selected)}.
              </p>
              <label>
                Interaction type
                <select value={kind} onChange={(e) => setKind(e.target.value)}>
                  {['Call', 'Email', 'Meeting', 'Note'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Description
                <textarea
                  required
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <button
                className="pi-primary"
                disabled={log.isPending || !note.trim()}
              >
                Save interaction
              </button>
              {log.isError && <p role="alert">Unable to save. Please retry.</p>}
            </form>
          ) : dialog === 'interaction' && openInteraction ? (
            <div className="pi-interaction-detail">
              <dl>
                <div>
                  <dt>Date</dt>
                  <dd>{humanDate(openInteraction.at) || 'Not on record'}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{openInteraction.kind || 'Not on record'}</dd>
                </div>
                <div>
                  <dt>Direction</dt>
                  <dd>{openInteraction.direction || 'Not on record'}</dd>
                </div>
                <div>
                  <dt>Recorded by</dt>
                  <dd>{openInteraction.owner || 'Not on record'}</dd>
                </div>
                <div>
                  <dt>Party</dt>
                  <dd>{selected ? partyName(selected) : 'Not on record'}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{openInteraction.source}</dd>
                </div>
              </dl>
              {/* The dialog's own title already carries the subject; a second
                  copy under it just pushes the body down. */}
              <h3>{/note/i.test(openInteraction.kind) ? 'Note' : 'Message'}</h3>
              <p className="pi-interaction-body">{openInteraction.detail}</p>
              {openInteraction.source === 'CRM correspondence' && (
                <p className="pi-empty">
                  This is the message preview the CRM stores. The full body is
                  not held on the contact record.
                </p>
              )}
              <button type="button" onClick={() => setDialog('history')}>
                ← All interactions ({history.length})
              </button>
            </div>
          ) : dialog === 'history' ? (
            <div>
              {history.length ? (
                history.map((item) => (
                  <article key={item.id}>
                    <button
                      type="button"
                      className="pi-interaction-open"
                      onClick={() => {
                        setOpenInteraction(item)
                        setDialog('interaction')
                      }}
                    >
                      <time>{humanDate(item.at)}</time>
                      <h3>{item.subject}</h3>
                      <p>{item.detail}</p>
                    </button>
                  </article>
                ))
              ) : (
                <p>No interactions recorded for this party.</p>
              )}
              <a
                href={`/pipeline?deal=${encodeURIComponent(loanId)}&tab=journal`}
                onClick={(e) => {
                  e.preventDefault()
                  setDialog(null)
                  setTab('journal')
                }}
              >
                Open complete deal journal →
              </a>
            </div>
          ) : dialog === 'remove' ? (
            <div>
              <p>
                Remove {selected && partyName(selected)} from this deal? The CRM
                contact will remain in the directory.
              </p>
              <button
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              <p>
                No screening assessment, source, findings or last-screened
                timestamp is supplied by the party roster.
              </p>
              <p>
                Scores remain unassessed until a governed source supplies the
                assessment. A CRM record or contact score does not establish
                compliance clearance.
              </p>
              {selected?.score != null && (
                <p>
                  Recorded CRM score: {selected.score}. Its assessment method
                  and scale are unavailable; it is not used as a risk or
                  reputation score.
                </p>
              )}
            </div>
          )}
        </PartyDialog>
      )}
    </div>
  )
}
